import { getApiUrl } from './utils';

interface ApiConfig {
  baseUrl: string;
  token: string;
}

// API Response Types
export interface ApiResponse<T> {
  list: T[];
  pageInfo?: {
    totalRows: number;
  };
}

export interface Order {
  Id: number;  // Database ID
  orderId: number;  // 6-digit order ID
  brideName: string;
  weddingDate: string;
  requiredDate: string;
  deliverTo: string;
  sendAddress: string;
  process: string;
  name: string;
  orderItem: string;
  Clients: number;
  Note: string;
}

export interface Client {
  Id: number;
  name: string;
  phone: string;
  email: string;
  shopAddress: string;
}

export interface Category {
  Id: number;
  Title: string;
  "Product-items": number;
}

export interface Product {
  Id: number;
  Title: string;
  Category: number;
}

export interface VeilLength {
  Id: number;
  Title: string;
}

export interface Color {
  Id: number;
  Title: string;
}

class ApiClient {
  private baseUrl: string;
  private token: string;

  constructor(config: ApiConfig) {
    this.baseUrl = getApiUrl(config.baseUrl);
    this.token = config.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'accept': 'application/json',
      'xc-token': this.token,
      ...options.headers,
    };

    try {
      if (import.meta.env.DEV) {
        console.log('Making API request:', {
          endpoint,
          method: options.method || 'GET'
        });
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      console.error('API request error:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  // Users/Clients
  async getUsers(): Promise<ApiResponse<Client>> {
    return this.request<ApiResponse<Client>>(`/tables/${import.meta.env.VITE_CLIENTS_TABLE}/records`);
  }

  async getUserById(id: string): Promise<Client> {
    return this.request<Client>(`/tables/${import.meta.env.VITE_CLIENTS_TABLE}/records/${id}`);
  }

  // Orders
  async getOrders(): Promise<ApiResponse<Order>> {
    return this.request<ApiResponse<Order>>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records`);
  }

  async getOrderById(id: string): Promise<Order> {
    return this.request<Order>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records/${id}`);
  }

  async createOrder(data: Partial<Order>): Promise<Order> {
    return this.request<Order>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateOrder(id: string, data: Partial<Order>): Promise<Order> {
    return this.request<Order>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async linkOrderToClient(clientId: number, orderId: number): Promise<any> {
    return this.request<any>(`/tables/${import.meta.env.VITE_CLIENTS_TABLE}/links/${import.meta.env.VITE_CLIENTS_ORDERS_LINK}/records/${clientId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify([{ Id: orderId }]),
    });
  }

  async fetchOrders(): Promise<ApiResponse<Order>> {
    const clientId = localStorage.getItem("clientId");
    if (!clientId) {
      throw new Error("No client ID found");
    }

    try {
      if (import.meta.env.DEV) {
        console.log("Fetching orders for client:", clientId);
      }

      // First get linked orders for the client
      const linkedOrders = await this.request<ApiResponse<{ Id: number }>>(`/tables/${import.meta.env.VITE_CLIENTS_TABLE}/links/${import.meta.env.VITE_CLIENTS_ORDERS_LINK}/records/${clientId}`);

      if (import.meta.env.DEV) {
        console.log("Linked orders response:", {
          count: linkedOrders.list?.length
        });
      }

      if (!linkedOrders?.list) {
        return { list: [], pageInfo: { totalRows: 0 } };
      }

      // Get the linked order IDs
      const linkedOrderIds = linkedOrders.list.map(order => order.Id);

      if (import.meta.env.DEV) {
        console.log("Linked order IDs:", linkedOrderIds);
      }

      if (linkedOrderIds.length === 0) {
        return { list: [], pageInfo: { totalRows: 0 } };
      }

      // Only fetch orders that belong to the current client
      const orderPromises = linkedOrderIds.map(orderId => 
        this.request<Order>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records/${orderId}`)
      );

      const orders = await Promise.all(orderPromises);

      if (import.meta.env.DEV) {
        console.log("Fetched orders:", orders.length);
      }

      const filteredOrders = orders.filter(Boolean);

      if (import.meta.env.DEV) {
        console.log("Final filtered orders:", filteredOrders.length);
      }

      return {
        list: filteredOrders,
        pageInfo: {
          totalRows: filteredOrders.length,
        },
      };
    } catch (error) {
      console.error("Error fetching orders:", error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async fetchCategories(): Promise<ApiResponse<Category>> {
    return this.request<ApiResponse<Category>>(`/tables/${import.meta.env.VITE_CATEGORIES_TABLE}/records`);
  }

  async fetchProductsByCategory(categoryId: string): Promise<ApiResponse<Product>> {
    try {
      // First request with limit=1 to get total count
      const initialResponse = await this.request<ApiResponse<Product>>(
        `/tables/${import.meta.env.VITE_CATEGORIES_TABLE}/links/${import.meta.env.VITE_CATEGORIES_PRODUCTS_LINK}/records/${categoryId}?limit=1`
      );
      
      // If there are no products or just one product, return the initial response
      if (!initialResponse.pageInfo || initialResponse.pageInfo.totalRows <= 1) {
        return initialResponse;
      }
      
      // Get all products in one request with sufficient limit
      const totalRows = initialResponse.pageInfo.totalRows;
      const allProductsResponse = await this.request<ApiResponse<Product>>(
        `/tables/${import.meta.env.VITE_CATEGORIES_TABLE}/links/${import.meta.env.VITE_CATEGORIES_PRODUCTS_LINK}/records/${categoryId}?limit=${totalRows}`
      );
      
      if (import.meta.env.DEV) {
        console.log(`Fetched all ${allProductsResponse.list.length} products for category ${categoryId}`);
      }
      
      return allProductsResponse;
    } catch (error) {
      console.error('Error fetching products by category:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async fetchVeilLengths(): Promise<ApiResponse<VeilLength>> {
    return this.request<ApiResponse<VeilLength>>(`/tables/${import.meta.env.VITE_VEIL_LENGTHS_TABLE}/records`);
  }

  async fetchColors(): Promise<ApiResponse<Color>> {
    return this.request<ApiResponse<Color>>(`/tables/${import.meta.env.VITE_COLORS_TABLE}/records`);
  }

  async updateOrderStatus(orderId: number, newStatus: "New" | "Contact Client" | "Processing" | "Deliver" | "Done"): Promise<Order> {
    return this.request<Order>(`/tables/${import.meta.env.VITE_ORDERS_TABLE}/records/${orderId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ process: newStatus }),
    });
  }

  async fetchClientData(clientId: string): Promise<Client> {
    if (import.meta.env.DEV) {
      console.log("Fetching client data for ID:", clientId);
    }

    try {
      const response = await this.request<Client>(`/tables/${import.meta.env.VITE_CLIENTS_TABLE}/records/${clientId}`);

      if (!response) {
        console.error("No client data returned");
        throw new Error("No client data found");
      }

      if (import.meta.env.DEV) {
        console.log("Client data fetched successfully:", {
          id: response.Id,
          hasData: !!response
        });
      }

      // Store email for order filtering
      localStorage.setItem("clientEmail", response.email);
      return response;
    } catch (error) {
      console.error("Error in fetchClientData:", error instanceof Error ? error.message : 'Unknown error');
      // Clear invalid session
      localStorage.removeItem("clientId");
      localStorage.removeItem("sessionToken");
      throw error;
    }
  }
}

// Create and export API client instance
export const api = new ApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  token: import.meta.env.VITE_API_TOKEN,
});

// Export common functions
export const fetchProductsByCategory = (categoryId: string) => api.fetchProductsByCategory(categoryId);
export const fetchCategories = () => api.fetchCategories();
export const fetchVeilLengths = () => api.fetchVeilLengths();
export const fetchColors = () => api.fetchColors();
export const fetchOrders = () => api.fetchOrders();
export const fetchClientData = (clientId: string) => api.fetchClientData(clientId);
export const linkOrderToClient = (clientId: number, orderId: number) => api.linkOrderToClient(clientId, orderId);
